/**
 * Matches `new Date().toISOString()` strings.
 *
 * @example 2023-04-03T06:30:05.973Z
 */
export const ISO_DATE_REGEX: RegExp = /\d\d\d\d-\d\d-\d\dT\d\d:\d\d:\d\d.\d\d\dZ/;

/**
 * A lenient semver regex that:
 * - Only requires a major number, but leaves minor and patch optional
 * - Allows an optional `-` pre-release suffix with _any_ set of characters
 *   afterwards, rather than those that the semver spec actually restricts.
 * - Does not include `^` and `$` operators to enforce that the entire string is
 *   valid, allowing it to match on substrings. This is needed because this
 *   regex's source is used to build up other combination regexes that look for
 *   semver substrings.
 *
 * @note This is duplicated in `@readme/api-contracts/core/constants.ts`. Please keep it in sync.
 * @see SEMVER_REGEX_STRICT for a stricter regex without these caveats
 *
 * @example 1
 * @example 1.1
 * @example 1.1.1
 * @example 4-debug.test
 * @example 1.1_invalid-chars // this is not valid semver, but will pass validation because the substring `1.1` matches
 */
export const SEMVER_REGEX: RegExp = /([0-9]+)(?:\.([0-9]+))?(?:\.([0-9]+))?(-.*)?/;

/**
 * A "relaxed" semver regex to test whether an entire string is a valid semver
 * value according to the official spec.
 *
 * This regex is copied from semver.org, but makes the minor and patch groups
 * optional via the `?` operator so that we accept semver values like `1` and
 * `1.0` rather than requiring the full `1.0.0`.
 *
 * @link
 * https://semver.org/#is-there-a-suggested-regular-expression-regex-to-check-a-semver-string
 *
 * @example 1
 * @example 1.1
 * @example 1.1.1
 * @example 1.1.1-prerelease
 * @example 1.1.1+build
 * @example 01.01.01
 *
 */
export const SEMVER_REGEX_STRICT: RegExp =
  /^(\d+)(\.(\d+))?(\.(\d+))?(?:-((?:\d+|[0-9a-zA-Z-]*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:\d+|[0-9a-zA-Z-]*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

/**
 * Extension of SEMVER_REGEX_STRICT to allow for an optional `-xx` language suffix
 * for i18n support.
 *
 * @example 1
 * @example 1.1-en
 * @example 1.1.1-fr
 * @example 1.1.1-prerelease-es
 * @example 1.1.1+build-zh
 * @example 01.01.01-ja
 */
export const SEMVER_LANG_REGEX: RegExp =
  /^(\d+)(\.(\d+))?(\.(\d+))?(?:-((?:\d+|[0-9a-zA-Z-]*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:\d+|[0-9a-zA-Z-]*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?(-[a-z]{2})?$/;
